import * as React from 'react'
import * as jotai from 'jotai'

import type { ApiResponse } from '@owl-nest/api-client'
import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'

export async function create(projectId: number): Promise<ApiResponse<api.Withdrawal>> {
  const response = await api.api.post.withdrawals({
    urlParams: { projectId: String(projectId) },
  })

  return response.next((success) => success.body)
}

// Get rid of the API `null` typing and enforce empty arrays instead
export type WithdrawalPage<WITH_BALANCE extends boolean = false> = { withdrawals: api.Withdrawal[] } & Omit<
  api.WithdrawalPage<WITH_BALANCE>,
  'withdrawals'
>

export type GetPageParams<WITH_BALANCE extends boolean = false> = {
  limit?: number
  withBalance?: WITH_BALANCE
}

export async function getPage<WITH_BALANCE extends boolean = false>(
  projectId: number,
  params: URLSearchParams,
): Promise<ApiResponse<WithdrawalPage<WITH_BALANCE>>> {
  const response = await api.api.get.withdrawals({
    getParams: params,
    urlParams: { projectId: String(projectId) },
    withToken: true,
  })

  return response.next(
    (success) => ({ ...success.body, withdrawals: success.body.withdrawals ?? [] }) as WithdrawalPage<WITH_BALANCE>,
  )
}

export const WITHDRAWAL_PAGE_CACHE = jotai.atom<hooks.Cache<WithdrawalPage<boolean>[]>>({})

type UseWithdrawalPage<WITH_BALANCE extends boolean = false> = {
  response: hooks.Response<WithdrawalPage<WITH_BALANCE>[]>
  query: () => void
  queryNext: () => Promise<void>
}

export function useWithdrawalPage<WITH_BALANCE extends boolean = false>(
  projectId: number | undefined,
  params: GetPageParams<WITH_BALANCE> = {},
): UseWithdrawalPage<WITH_BALANCE> {
  const {
    cache: responseWithdrawalPage,
    query: queryWithdrawalPage,
    patch: patchWithdrawalPage,
    getCurrentResponse,
  } = hooks.useSharedQuery(
    async (projectId: number, params: GetPageParams<WITH_BALANCE>) => {
      const response = await getPage<WITH_BALANCE>(projectId, toURLSearchParams(params))
      return response.next((success) => [success])
    },
    {
      atom: WITHDRAWAL_PAGE_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount: true, query, getResponse }, id(projectId, params))

  return { response: getResponse(), query, queryNext }

  function query(): Promise<void> | undefined {
    if (projectId) return queryWithdrawalPage(projectId, params)
  }

  function id(projectId: number | undefined, params: GetPageParams<WITH_BALANCE>): string {
    return JSON.stringify([projectId, params.withBalance])
  }

  function getResponse(): hooks.Response<WithdrawalPage<WITH_BALANCE>[]> {
    if (projectId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    const response = getCurrentResponse([projectId, params])

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response as hooks.Response<WithdrawalPage<WITH_BALANCE>[]>
  }

  async function queryNext(): Promise<void> {
    if (projectId === undefined) {
      return
    }

    const response = getResponse()
    const metaNext =
      response?.data !== undefined ? response.data[response.data.length - 1].meta?.next ?? undefined : undefined
    const nextSearchParam = new URLSearchParams(metaNext)

    if (metaNext === undefined) {
      console.warn(`[services/order] queryNext(), next don't exist`)
      return
    }

    patchWithdrawalPage([projectId, params], async (response) => {
      const getPageResponse = await getPage(projectId, nextSearchParam)

      return getPageResponse.next((success) => {
        let pages = []
        if (response.data !== undefined) {
          pages = [...response.data, success]
        } else {
          pages = [success]
        }

        return pages
      })
    })
  }
}

function toURLSearchParams<WITH_BALANCE extends boolean = false>(params: GetPageParams<WITH_BALANCE>): URLSearchParams {
  const searchParams = new URLSearchParams()

  if (params === undefined) {
    return searchParams
  }

  if (params.withBalance) {
    searchParams.set('with_balance', String(params.withBalance))
  }

  if (params.limit) {
    searchParams.set('limit', String(params.limit))
  }

  return searchParams
}
